import {
    Add,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GET } from "../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../Utilities/MultiPermissionAuthorize";
import { currencyFormatter } from "../Reading/Bill/Cells/Last12Summary";
import { dateFormatter } from "../Reading/Bill/SingleBill";
import BaseTable from "../Table/BaseTable";
import SearchBar from "../Table/Components/SearchBar";
const initialClearAll = {
    search: false,
    startDate: false,
    endDate: false,
};

const AdvancePayment = () => {
    const [filter, setFilter] = useState({
        search: "",
        meterNo: "",
        startDate: "",
        endDate: "",
    });
    const [clearAll, setClearAll] = useState(initialClearAll);

    const handleClearAll = () => {
        // reset UI filters

        // reset applied filters
        setFilter({ ...filter, search: "" });

        // notify searchable inputs
        setClearAll({
            search: true,
            startDate: true,
            endDate: true,
        });

        setFilter({ ...filter, search: "", startDate: "", endDate: "" });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        // reset flags after one render
        setTimeout(() => setClearAll(initialClearAll), 0);
    };

    const [rowsCount, setRowsCount] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, limit: 100 });
    const [changeCount, setChangeCount] = useState(0);
    const [feedback, setFeedback] = useState("");

    const getAdvancePayment = () => {
        return GET("/advance", {
            page: pagination.page + 1,
            limit: pagination.limit,
            meterNo: filter.search || undefined,
            startDate: filter.startDate ? filter.startDate : undefined,
            endDate: filter.endDate ? filter.endDate : undefined,
        });
    };

    const { data, isLoading } = useQuery(
        ["advance", pagination.page, pagination.limit, filter],
        getAdvancePayment,
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];
        if (!data?.data?.rows || !Array.isArray(data.data.rows))
            return summaryRow;

        let advanceBalance = 0;
        let advanceBalancedUsed = 0;
        let advanceBalanceRemaining = 0;

        for (const row of data?.data.rows) {
            advanceBalance += row.advanceBalance;
            advanceBalancedUsed += row.advanceBalancedUsed;
            advanceBalanceRemaining +=
                row.advanceBalance - row.advanceBalancedUsed;
        }

        for (const col of cols) {
            switch (col) {
                case "advanceBalance":
                    summaryRow.push(currencyFormatter.format(advanceBalance));
                    break;
                case "advanceBalancedUsed":
                    summaryRow.push(
                        currencyFormatter.format(advanceBalancedUsed)
                    );
                    break;

                case "advanceBalanceRemaining":
                    summaryRow.push(
                        currencyFormatter.format(advanceBalanceRemaining)
                    );
                    break;
                default:
                    summaryRow.push("");
            }
        }
        return summaryRow;
    };

    // When user clicks Search button
    useEffect(() => {
        if (changeCount > 0) {
            setFilter((prev) => ({
                ...prev,
                meterNo: prev.search,
            }));
        }
    }, [changeCount]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ p: 2, mb: "1rem" }}>
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md="auto">
                        <Typography color="GrayText" variant="h5">
                            Advance Payment
                        </Typography>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md
                        container
                        spacing={1.5}
                        justifyContent="flex-end"
                    >
                        <Grid item xs={12} md="auto">
                            <MultiPermissionAuthorize ops={["CREATE ADVANCE"]}>
                                <Link
                                    to="/advance/create"
                                    style={{ textDecoration: "none" }}
                                >
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<Add />}
                                        sx={{ minWidth: "20ch" }}
                                        fullWidth
                                    >
                                        Add Advance
                                    </Button>
                                </Link>
                            </MultiPermissionAuthorize>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item container xs={12} spacing={1.5} pt={2}>
                    <Grid item xs={12} md={4}>
                        <SearchBar
                            filter={filter}
                            setFilter={setFilter}
                            clear={clearAll.search}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Start Date"
                            name="startDate"
                            InputLabelProps={{ shrink: true }}
                            value={filter.startDate}
                            type="date"
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    startDate: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="End Date"
                            name="endDate"
                            InputLabelProps={{ shrink: true }}
                            value={filter.endDate}
                            type="date"
                            onChange={(e) =>
                                setFilter({
                                    ...filter,
                                    endDate: e.target.value,
                                })
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            startIcon={<FilterAltOutlined />}
                            onClick={() => setChangeCount((prev) => prev + 1)}
                            sx={{ minWidth: "20ch", height: "100%" }}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{ minWidth: "20ch", height: "100%" }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>

                {filter.search === "" ? (
                    <Alert severity="info" style={{ marginTop: "1rem" }}>
                        Must provide Meter No. in order to view advance payment
                        leisure.
                    </Alert>
                ) : (
                    <Box sx={{ height: "70%" }}>
                        <BaseTable
                            headers={{
                                id: "ID",
                                meterNo: "Meter No.",
                                name: "Name",
                                fatherName: "Father Name",
                                cnic: "CNIC",

                                mobileNo: "mobileNumber",
                                createdAt: "createdAt",
                                advanceBalance: "Advance Balance",
                                advanceBalancedUsed: "Advance Balance Used",
                                advanceBalanceRemaining:
                                    "Advance Balance Remaining",
                                paymentMethod: "PaymentMethod",
                            }}
                            data={
                                data &&
                                data?.data.rows.map((row: any) => ({
                                    ...row,
                                    advanceBalance: row.advanceBalance,
                                    advanceBalancedUsed:
                                        row.advanceBalancedUsed,
                                    advanceBalanceRemaining:
                                        row.advanceBalanceRemaining,
                                    meterNo: row.meter.meterNo,
                                    name: row?.meter?.property?.customer?.name,
                                    fatherName:
                                        row?.meter?.property?.customer
                                            ?.fatherName,
                                    cnic: row?.meter?.property?.customer?.cnic,
                                    mobileNo:
                                        row?.meter?.property?.customer
                                            ?.mobileNo,
                                    createdAt: dateFormatter.format(
                                        new Date(row.createdAt)
                                    ),
                                    paymentMethod: row?.paymentMethod?.name,
                                }))
                            }
                            delEndPoint="/advance"
                            defaultSelectedHeaders={["id"]}
                            feedback={feedback}
                            setFeedback={setFeedback}
                            rowsCount={rowsCount}
                            pagination={pagination}
                            setPagination={setPagination}
                            isLoading={isLoading}
                            load={Boolean(changeCount)}
                            summaryRow={createSummary}
                            permissions={{
                                edit: ["UPDATE ADVANCE"],
                                delete: ["DELETE ADVANCE"],
                            }}
                            queryKey={[
                                "advance",
                                pagination.page,
                                pagination.limit,
                                filter,
                            ]}
                            reportName="AdvancePayment__List"
                        />
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default AdvancePayment;
